import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import {
  Warning, ArrowRight, CurrencyDollar,
  ChatsCircle, Robot, BookOpen, ArrowUpRight,
} from "@phosphor-icons/react";
import { fetchMessages, fetchUsage } from "@/lib/api";
import ChannelAvatar, { ChannelBadge } from "@/components/ChannelAvatar";

function ProgressRing({ value = 0, size = 56, stroke = 5 }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.max(0, Math.min(100, value));
  const offset = circumference * (1 - pct / 100);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="hsl(var(--secondary))"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-primary">
        {pct}%
      </div>
    </div>
  );
}

function StatCard({ label, value, accent, icon: Icon, testid }) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-border-soft shadow-soft transition-shadow hover:shadow-card" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground font-medium">{label}</div>
        {Icon && (
          <div className="w-9 h-9 rounded-xl bg-secondary flex items-center justify-center text-primary">
            <Icon size={18} weight="duotone" />
          </div>
        )}
      </div>
      <div
        className={`font-display font-semibold text-3xl sm:text-4xl mt-3 ${accent || ""}`}
      >
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    escalated: "bg-destructive/10 text-destructive",
    auto_replied: "bg-primary/10 text-primary",
    owner_replied: "bg-accent/15 text-accent",
    pending: "bg-muted text-muted-foreground",
  };
  const labels = {
    escalated: "Eskaliert",
    auto_replied: "Bot",
    owner_replied: "Du",
    pending: "Ausstehend",
  };
  return (
    <span
      className={`px-2 py-0.5 text-[11px] font-medium rounded-full ${map[status] || map.pending}`}
    >
      {labels[status] || labels.pending}
    </span>
  );
}

export default function Overview() {
  const { stats } = useOutletContext() || {};
  const [recent, setRecent] = useState([]);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    fetchMessages({ limit: 6 }).then(setRecent).catch(() => {});
    fetchUsage().then(setUsage).catch(() => {});
  }, [stats?.total_messages]);

  const fmtUsd = (n) => `$${(n ?? 0).toFixed(n >= 1 ? 2 : 4)}`;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-6xl mx-auto" data-testid="overview-page">
      {/* Headline */}
      <div className="mb-8">
        <h1 className="font-semibold text-3xl sm:text-4xl tracking-tight">
          Übersicht
        </h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Dein Bot beantwortet Standard-Fragen auf WhatsApp und Email automatisch.
          Bei Unsicherheit oder Reklamationen wird an dich eskaliert.
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
        <StatCard label="Gesamt" value={stats?.total_messages ?? "—"} icon={ChatsCircle} testid="stat-total" />
        <StatCard label="Vom Bot" value={stats?.auto_replied ?? "—"} icon={Robot} testid="stat-auto" />
        <StatCard
          label="Eskaliert"
          value={stats?.escalated ?? "—"}
          icon={Warning}
          accent={(stats?.escalated ?? 0) > 0 ? "text-destructive" : ""}
          testid="stat-escalated"
        />
        <StatCard label="FAQ-Einträge" value={stats?.faq_count ?? "—"} icon={BookOpen} testid="stat-faqs" />
      </div>

      {/* Cost-Tracking */}
      {usage && (
        <div className="bg-white rounded-2xl p-6 border border-border-soft shadow-soft mb-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
              <CurrencyDollar size={16} weight="duotone" className="text-accent" />
            </div>
            <div className="font-display font-semibold text-lg">Anthropic API Kosten</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <div className="text-xs text-muted-foreground">Heute</div>
              <div className="font-display font-semibold text-2xl mt-1">{fmtUsd(usage.today.cost_usd)}</div>
              <div className="text-xs text-muted-foreground">{usage.today.calls} Calls</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">7 Tage</div>
              <div className="font-display font-semibold text-2xl mt-1">{fmtUsd(usage.last_7_days.cost_usd)}</div>
              <div className="text-xs text-muted-foreground">{usage.last_7_days.calls} Calls</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">30 Tage</div>
              <div className="font-display font-semibold text-2xl mt-1">{fmtUsd(usage.last_30_days.cost_usd)}</div>
              <div className="text-xs text-muted-foreground">{usage.last_30_days.calls} Calls</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Gesamt</div>
              <div className="font-display font-semibold text-2xl mt-1 text-primary">{fmtUsd(usage.total.cost_usd)}</div>
              <div className="text-xs text-muted-foreground">
                {usage.total.calls} Calls
                {usage.total.errors > 0 && <span className="text-destructive"> · {usage.total.errors} Fehler</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Channel breakdown */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-8">
        <div className="bg-white rounded-2xl p-6 border border-border-soft shadow-soft">
          <div className="flex items-center gap-2 mb-1">
            <ChannelBadge channel="whatsapp" size={14} />
          </div>
          <div className="flex items-baseline gap-2 mt-2">
            <div className="font-display font-semibold text-3xl">
              {stats?.whatsapp_count ?? 0}
            </div>
            <div className="text-sm text-muted-foreground">empfangen</div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-border-soft shadow-soft">
          <div className="flex items-center gap-2 mb-1">
            <ChannelBadge channel="email" size={14} />
          </div>
          <div className="flex items-baseline gap-2 mt-2">
            <div className="font-display font-semibold text-3xl">
              {stats?.email_count ?? 0}
            </div>
            <div className="text-sm text-muted-foreground">empfangen</div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-border-soft shadow-soft flex items-center gap-4">
          <ProgressRing value={stats?.auto_reply_rate ?? 0} />
          <div className="flex-1">
            <div className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Auto-Quote</div>
            <div className="font-display font-semibold text-3xl mt-1 text-primary">
              {stats?.auto_reply_rate ?? 0}%
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">ohne deine Hilfe gelöst</div>
          </div>
        </div>
      </div>

      {/* Recent activity */}
      <div className="bg-white rounded-2xl border border-border-soft shadow-soft overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-border-soft">
          <div className="flex items-center gap-2">
            <ChatsCircle size={18} weight="duotone" className="text-muted-foreground" />
            <div className="font-display font-semibold text-lg">Letzte Nachrichten</div>
          </div>
          <Link
            to="/inbox"
            className="text-sm text-primary inline-flex items-center gap-1 hover:underline"
            data-testid="view-inbox-link"
          >
            Inbox öffnen <ArrowUpRight size={14} weight="bold" />
          </Link>
        </div>
        {recent.length === 0 && (
          <div className="p-10 text-center text-sm text-muted-foreground">
            Noch keine Nachrichten. Probier den{" "}
            <Link to="/simulator" className="underline text-primary">
              Simulator
            </Link>
            .
          </div>
        )}
        {recent.map((m) => (
          <Link
            key={m.id}
            to={`/inbox/${m.conversation_id || m.id}`}
            className="flex gap-3 items-center p-4 border-b border-border-soft last:border-b-0 hover:bg-secondary/40 transition-colors"
            data-testid={`recent-msg-${m.id}`}
          >
            <ChannelAvatar channel={m.channel} name={m.sender_name} size={36} />
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{m.sender_name}</div>
              <div className="text-sm text-muted-foreground truncate">{m.body}</div>
            </div>
            <StatusBadge status={m.status} />
          </Link>
        ))}
      </div>

      {/* Escalation callout */}
      {(stats?.escalated ?? 0) > 0 && (
        <div className="mt-6 bg-destructive/5 border border-destructive/30 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 shadow-sm">
          <div className="w-11 h-11 rounded-xl bg-destructive/10 flex items-center justify-center shrink-0">
            <Warning size={22} weight="fill" className="text-destructive" />
          </div>
          <div className="flex-1">
            <div className="font-medium">
              {stats.escalated} Nachricht{stats.escalated > 1 ? "en" : ""} warten auf dich
            </div>
            <div className="text-sm text-muted-foreground">
              Der Bot war unsicher und konnte nicht selbst antworten.
            </div>
          </div>
          <Link
            to="/inbox?filter=escalated"
            className="btn-invert w-full sm:w-auto justify-center"
            data-testid="review-escalations-btn"
          >
            Anschauen
          </Link>
        </div>
      )}
    </div>
  );
}
